import React, { useState } from "react";
import { useUserShippingData } from "./useUserShippingData";
import { useAuth } from "../../../contexts/AuthContext";
import Button from "../../../components/controls/Button";
import FormInput from "../../../components/controls/FormInput";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import DropdownMenu from "../../../components/controls/DropdownMenu";

const KG_PER_LB = 0.45359237;
const CM_PER_IN = 2.54;
const CM_PER_FT = 30.48;

const toKg = (v, unit) =>
  unit === "lb" ? (Number(v) || 0) * KG_PER_LB : Number(v) || 0;

const toCm = (v, unit) =>
  unit === "in"
    ? (Number(v) || 0) * CM_PER_IN
    : unit === "ft"
    ? (Number(v) || 0) * CM_PER_FT
    : Number(v) || 0;

const toInputString = (v) => {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  return Number.isFinite(n) ? `${n}` : "";
};

const renderSelectLabel = (label) => (
  <label className="block text-sm mb-1 text-primary">{label}</label>
);

const BaseSelect = ({ value, onChange, children, additionalClass = "" }) => (
  <div className="relative w-full">
    <select
      value={value}
      onChange={onChange}
      className={`w-full appearance-none bg-white text-primary border border-gray-300 rounded-lg px-4 pr-10 py-3 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary ${additionalClass}`}
    >
      {children}
    </select>

    <ChevronDownIcon className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
  </div>
);

export default function ShippingSection({ formData, onChange }) {
  const hasShipping = Boolean(formData.hasShipping);

  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(false);

  const { currentUser } = useAuth();
  const currentUserId = currentUser?.id;
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const { carriers, warehouses } = useUserShippingData(API_BASE, currentUserId);

  const weightUnit = formData.weightUnit || "kg";
  const lengthUnit = formData.lengthUnit || "cm";

  const display = {
    weight:
      weightUnit === "lb"
        ? (formData.weightKg ?? 0) / KG_PER_LB
        : formData.weightKg ?? 0,
    L:
      lengthUnit === "in"
        ? (formData.lengthCm ?? 0) / CM_PER_IN
        : lengthUnit === "ft"
        ? (formData.lengthCm ?? 0) / CM_PER_FT
        : formData.lengthCm ?? 0,
    W:
      lengthUnit === "in"
        ? (formData.widthCm ?? 0) / CM_PER_IN
        : lengthUnit === "ft"
        ? (formData.widthCm ?? 0) / CM_PER_FT
        : formData.widthCm ?? 0,
    H:
      lengthUnit === "in"
        ? (formData.heightCm ?? 0) / CM_PER_IN
        : lengthUnit === "ft"
        ? (formData.heightCm ?? 0) / CM_PER_FT
        : formData.heightCm ?? 0,
  };

  const renderShippingChoice = (
    <div className="bg-gray-50 p-4 border rounded-lg space-y-3 mb-4">
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          className="checkbox-pro"
          checked={hasShipping}
          onChange={(e) => onChange("hasShipping", e.target.checked)}
        />
        <span className="font-medium text-primary">
          Ce produit nécessite une livraison
        </span>
      </label>

      {!hasShipping && (
        <div className="p-2 rounded-md text-sm text-gray-700">
          Remise en mains propres — aucune livraison nécessaire.
          <p className="text-xs text-gray-500 mt-1">
            Aucun transporteur ou tarif ne sera calculé lors de la commande.
          </p>
        </div>
      )}
    </div>
  );

  if (!hasShipping) {
    return (
      <section className="bg-white/70 rounded-lg p-4 border space-y-5">
        <h2 className="text-lg font-semibold text-primary">Expédition</h2>
        {renderShippingChoice}
      </section>
    );
  }

  const getRates = async () => {
    setLoading(true);
    setRates(null);

    try {
      const payload = {
        carrierAccountId: formData.carrierAccountId || null,
        warehouseId: formData.warehouseId || null,
        weightKg: Number(formData.weightKg) || 0,
        lengthCm: Number(formData.lengthCm) || 0,
        widthCm: Number(formData.widthCm) || 0,
        heightCm: Number(formData.heightCm) || 0,
        to: {
          countryCode: "CA",
          provinceCode: "QC",
          city: "Montréal",
          postalCode: "H1V0B2",
          line1: "",
          line2: "",
        },
      };

      const res = await fetch(`${API_BASE}/api/shipping/rates`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      setRates(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Erreur getRates :", err);
      setRates([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-white/70 rounded-lg p-4 border space-y-5">
      <h2 className="text-lg font-semibold text-primary">Expédition</h2>
      {renderShippingChoice}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="w-full">
          <label className="block text-sm mb-1 text-primary">
            Transporteur
          </label>
          <div className="w-full">
            <DropdownMenu
              title={
                carriers.find(
                  (c) => `${c.id}` === `${formData.carrierAccountId}`
                )?.accountNickname ||
                carriers.find(
                  (c) => `${c.id}` === `${formData.carrierAccountId}`
                )?.name ||
                ""
              }
              isDropdown={true}
              items={[
                { label: "", onClick: () => onChange("carrierAccountId", "") },
                ...carriers.map((c) => ({
                  label: c.accountNickname ?? c.name,
                  onClick: () => onChange("carrierAccountId", c.id),
                })),
              ]}
            />
          </div>
        </div>

        <div className="w-full">
          <label className="block text-sm mb-1 text-primary">
            Entrepôt d’envoi
          </label>
          <div className="w-full">
            <DropdownMenu
              title={
                warehouses.find((w) => `${w.id}` === `${formData.warehouseId}`)
                  ?.label ||
                warehouses.find((w) => `${w.id}` === `${formData.warehouseId}`)
                  ?.name ||
                warehouses.find(
                  (w) => `${w.id}` === `${formData.warehouseId}`
                )?.["(Entrepôt)"] ||
                ""
              }
              isDropdown={true}
              items={[
                { label: "", onClick: () => onChange("warehouseId", "") },
                ...warehouses.map((w) => ({
                  label: w.label ?? w.name ?? "(Entrepôt)",
                  onClick: () => onChange("warehouseId", w.id),
                })),
              ]}
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm mb-1 text-primary">Poids</label>

        <div className="flex space-between gap-4">
          <div className="w-full">
            <FormInput
              label=""
              name="weightDisplay"
              type="number"
              value={toInputString(display.weight)}
              onChange={(_, v) => onChange("weightKg", toKg(v, weightUnit))}
              showRequiredOrOptional={false}
              additionalClass="h-12 w-full"
            />
          </div>

          <div className="w-full">
            <DropdownMenu
              title={weightUnit === "lb" ? "lb" : "kg"}
              isDropdown={true}
              items={[
                { label: "kg", onClick: () => onChange("weightUnit", "kg") },
                { label: "lb", onClick: () => onChange("weightUnit", "lb") },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-start">
        <FormInput
          label="Longueur"
          name="lengthDisplay"
          type="number"
          value={toInputString(display.L)}
          onChange={(_, v) => onChange("lengthCm", toCm(v, lengthUnit))}
          showRequiredOrOptional={false}
        />

        <FormInput
          label="Largeur"
          name="widthDisplay"
          type="number"
          value={toInputString(display.W)}
          onChange={(_, v) => onChange("widthCm", toCm(v, lengthUnit))}
          showRequiredOrOptional={false}
        />

        <FormInput
          label="Hauteur"
          name="heightDisplay"
          type="number"
          value={toInputString(display.H)}
          onChange={(_, v) => onChange("heightCm", toCm(v, lengthUnit))}
          showRequiredOrOptional={false}
        />

        <div className="w-full">
          <label className="block text-sm text-primary">Unité</label>
          <div className="">
            <DropdownMenu
              title={
                lengthUnit === "in"
                  ? "Pouces (in)"
                  : lengthUnit === "ft"
                  ? "Pieds (ft)"
                  : "cm"
              }
              isDropdown={true}
              items={[
                { label: "cm", onClick: () => onChange("lengthUnit", "cm") },
                {
                  label: "Pouces (in)",
                  onClick: () => onChange("lengthUnit", "in"),
                },
                {
                  label: "Pieds (ft)",
                  onClick: () => onChange("lengthUnit", "ft"),
                },
              ]}
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          as="button"
          type="button"
          color="primary"
          size="md"
          disabled={loading}
          onClick={getRates}
        >
          {loading ? "Calcul..." : "Obtenir les tarifs"}
        </Button>
      </div>

      {rates && (
        <div className="mt-2">
          <h3 className="font-medium mb-2">
            Options & tarifs (Adresse test : H1V 0B2, Montréal, QC) :
          </h3>

          {rates.length === 0 ? (
            <div className="text-sm text-gray-500">Aucun tarif retourné.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[...rates]
                .sort((a, b) => (a.total ?? 0) - (b.total ?? 0))
                .map((r, i) => {
                  const best = i === 0;

                  return (
                    <div
                      key={`${r.carrier}-${r.serviceCode}-${i}`}
                      className={`rounded-xl border p-4 shadow-sm bg-white ${
                        best
                          ? "border-emerald-400 ring-1 ring-emerald-200 bg-emerald-50"
                          : "border-gray-200"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className="text-sm text-gray-600">
                          {r.carrier || "—"}
                        </div>

                        {best && (
                          <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200">
                            Meilleur prix
                          </span>
                        )}
                      </div>

                      <div className="text-base font-semibold">
                        {r.serviceName}
                      </div>

                      <div className="mt-2 text-2xl font-bold">
                        {new Intl.NumberFormat("fr-CA", {
                          style: "currency",
                          currency: r.currency || "CAD",
                        }).format(r.total || 0)}
                      </div>

                      <div className="mt-1 text-sm text-gray-600">
                        Arrivée estimée :{" "}
                        {r.expectedDeliveryDate
                          ? new Date(r.expectedDeliveryDate).toLocaleDateString(
                              "fr-CA",
                              {
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                              }
                            )
                          : "—"}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
